import React, { useState, useEffect } from 'react';

import {
    Box,
    Typography,
    Button,
    TextField,
    IconButton,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    OutlinedInput,
    MenuItem,
    ListSubheader,
    ListItemText,
    Checkbox,
    Radio,
    Switch,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Chip,
} from "@mui/material";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

import BookCover from './BookCover';
import useSortHead from './useSortHead';

import { formatNumberWithSpaces, formatHumanTime, normalizeForSearch, deriveBookLevel, bookLevelNames } from './helpers';

// Tags handled by the dropdown's Difficulty group (via deriveBookLevel)
// rather than listed as content tags; 'hidden' books are never shown and
// 'free' only concerns consumer subscriptions.
const nonContentTags = ['beginner', 'intermediate', 'advanced', 'literature', 'hidden', 'free'];

const AssignmentBook = ({ setActiveStep, books, selectedBookId, setSelectedBookId, setDisabledButtons, booksLoading, errorMessage }) => {
    const { TableCellSortHead, sortFunction } = useSortHead();
    const [showInfo, setShowInfo] = useState(null);
    const [filter, setFilter] = useState('');
    // Levels are stored as 'level:N' so they can share the dropdown's value
    // array with plain content tags.
    const [selectedTagFilters, setSelectedTagFilters] = useState([]);
    const [multiSelect, setMultiSelect] = useState(true);
    const [tagMenuOpen, setTagMenuOpen] = useState(false);

    const searchWords = normalizeForSearch(filter).split(' ').filter(Boolean);
    const selectedLevels = selectedTagFilters
        .filter(value => value.startsWith('level:'))
        .map(value => Number(value.slice(6)));
    const selectedTags = selectedTagFilters.filter(value => !value.startsWith('level:'));

    const averageTokensPerSecond = (array => {
        let totalTokens = 0;
        let totalSeconds = 0;

        for (let i = 0; i < array.length; i++) {
            if (array[i].tokens && array[i].duration) {
                totalTokens += array[i].tokens;
                totalSeconds += array[i].duration;
            }
        }

        return totalTokens / totalSeconds;
    })(books);

    const visibleBooks = books.filter(book => {
        if (!book.cover || !book.cover.startsWith('https://')) {
            return false;
        }
        return !(book.tags && book.tags.split(';').includes('hidden'));
    });

    const tagCounts = {};
    visibleBooks.forEach(book => (book.tags || '').split(';').forEach(tag => {
        if (tag && !nonContentTags.includes(tag)) {
            tagCounts[tag] = (tagCounts[tag] || 0) + 1;
        }
    }));
    const tagOptions = Object.keys(tagCounts)
        .sort((a, b) => tagCounts[b] - tagCounts[a] || a.localeCompare(b));

    // Within each dropdown group any selected entry may match; a level
    // selection and a tag selection must both hold.
    const sortedFilteredBooks = visibleBooks.filter(book => {
        const bookData = normalizeForSearch(book.title + ' ' + book.author);
        if (!searchWords.every(word => bookData.includes(word))) {
            return false;
        }
        if (selectedLevels.length > 0 && !selectedLevels.includes(deriveBookLevel(book))) {
            return false;
        }
        if (selectedTags.length > 0) {
            const bookTags = (book.tags || '').split(';');
            if (!selectedTags.some(tag => bookTags.includes(tag))) {
                return false;
            }
        }
        return true;
    }).sort((a, b) => {
        if (!a.tags && b.tags) {
            return 1;
        }
        if (a.tags && !b.tags) {
            return -1;
        }
        return sortFunction(a, b);
    }).map(book => {
        book.length = Math.round((averageTokensPerSecond * book.duration + book.tokens) / 2);
        return book;
    });

    // A filter change can hide the selected title, which would otherwise stay
    // invisibly selected behind an enabled Continue button. Keyed on the
    // filters only, so an edited assignment's book (which may no longer be
    // listed at all) is left alone until the user actually filters.
    useEffect(() => {
        if (selectedBookId !== null && !sortedFilteredBooks.some(book => book.id === selectedBookId)) {
            setSelectedBookId(null);
        }
    }, [filter, selectedTagFilters]);

    const handleFilterChange = (event) => {
        setFilter(event.target.value);
    };

    // The Select always reports a multi-select style value array; in
    // single-select mode the entry the click toggled replaces the selection
    // outright (so re-picking the current entry keeps it, like a radio).
    // Clicks on the group subheaders also reach onChange, as an undefined
    // entry — drop those.
    const handleTagFilterChange = (event) => {
        const value = event.target.value.filter(entry => entry !== undefined);
        if (multiSelect) {
            setSelectedTagFilters(value);
            return;
        }
        const clicked = value.length > selectedTagFilters.length
            ? value.find(entry => !selectedTagFilters.includes(entry))
            : selectedTagFilters.find(entry => !value.includes(entry));
        if (!clicked) {
            return;
        }
        setSelectedTagFilters([clicked]);
        setTagMenuOpen(false);
    };

    const handleMultiSelectToggle = (event) => {
        const multi = event.target.checked;
        setMultiSelect(multi);
        if (!multi && selectedTagFilters.length > 1) {
            setSelectedTagFilters([selectedTagFilters[selectedTagFilters.length - 1]]);
        }
    };

    const handleInfoClick = (event, book) => {
        setShowInfo(book);
        setDisabledButtons(true);
        event.stopPropagation();
    };

    const onInfoClose = (event) => {
        setShowInfo(null);
        setDisabledButtons(false);
        event.stopPropagation();
    };

    const handleNext = (event) => {
        event.stopPropagation();
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleTableClick = (event) => {
        event.stopPropagation();
    };

    const tagColors = {
        translation: '#EAEC89', // Light Yellow: Suggests clarity and understanding.
        audio: '#B7D4FF', // Light Blue: Associated with tranquility and reliability, like audio books provide.
        commentary: '#FDB6E9', // Light Pink: Conveys open-mindedness and neutrality.
        literature: '#FFE4BB', // Light Orange: Evokes creativity and enthusiasm.
        poetry: '#FFB7D9', // Light Rose: Evokes emotions and sensitivity, like poetry often does.
        glossary: '#C8D9C8', // Light Green: Represents learning and growth.
        reader: '#D4B5FF', // Light Purple: Conveys wisdom and respect.
        fables: '#BFFFD9', // Mint Green: Associated with magic, storytelling and creativity.
        history: '#D4D4D4', // Light Grey: Conveys something established, reliable and timeless.
        anecdotes: '#FFD4B5', // Light Coral: Represents warmth, openness, sharing.
        'fairy tale': '#B5D4FF', // Sky Blue: Associated with dreaminess, magic and fantasy.
        mythology: '#ECD4FF', // Lavender: Often associated with the mysterious and the spiritual, which fits with mythology.
        stories: '#FFD4D4', // Light Red: Evokes excitement and passion, akin to the feelings stories often incite.
    };

    const renderTags = (tagstring) => <>
        {tagstring ? tagstring.split(';').map((tag, index) => <Chip
            key={index}
            size="small"
            label={tag}
            sx={{
                borderRadius: 2,
                backgroundColor: tagColors[tag],
                margin: '2px',
            }}
        />) : null}
    </>;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4, pb: 0, pt: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h5" sx={{ flexGrow: 1 }}>Select Text</Typography>
            </Box>

            <Typography variant="body1">
                Select a title in the list.
            </Typography>

            <Box
                sx={{ display: 'flex', width: '100%', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1 }}
                onClick={handleTableClick}
            >
                <TextField
                    onChange={handleFilterChange}
                    value={filter}
                    sx={{ width: 200 }}
                    placeholder="Search…"
                    inputProps={{ 'aria-label': 'search' }}
                    size='small'
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: filter ? (
                            <InputAdornment position="end">
                                <IconButton size="small" aria-label="clear search" onClick={() => setFilter('')}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />
                <FormControl size="small" sx={{ minWidth: 220, maxWidth: 340 }}>
                    <InputLabel id="tag-filter-label">Filter by tags</InputLabel>
                    <Select
                        labelId="tag-filter-label"
                        multiple
                        open={tagMenuOpen}
                        onOpen={() => setTagMenuOpen(true)}
                        onClose={() => setTagMenuOpen(false)}
                        value={selectedTagFilters}
                        onChange={handleTagFilterChange}
                        input={<OutlinedInput label="Filter by tags" />}
                        renderValue={(selected) => selected
                            .map(value => value.startsWith('level:') ? bookLevelNames[value.slice(6)] : value)
                            .join(', ')}
                        MenuProps={{
                            PaperProps: { sx: { maxHeight: 420 } },
                            // As a list subheader rather than a Select child, this
                            // row is not cloned into an option (no role="option",
                            // no autofocus, no onChange for its clicks).
                            MenuListProps: {
                                subheader: (
                                    <ListSubheader sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1, pt: 1 }}>
                                        <FormControlLabel
                                            sx={{ ml: 0 }}
                                            control={<Switch size="small" checked={multiSelect} onChange={handleMultiSelectToggle} />}
                                            label={<Typography variant="body2">Select several</Typography>}
                                        />
                                        <Button
                                            size="small"
                                            disabled={selectedTagFilters.length === 0}
                                            onClick={() => setSelectedTagFilters([])}
                                        >
                                            Clear
                                        </Button>
                                    </ListSubheader>
                                ),
                            },
                        }}
                    >
                        <ListSubheader>Difficulty</ListSubheader>
                        {Object.entries(bookLevelNames).map(([level, name]) => {
                            const checked = selectedLevels.includes(Number(level));
                            return (
                                <MenuItem key={level} value={`level:${level}`} dense>
                                    {multiSelect
                                        ? <Checkbox size="small" checked={checked} />
                                        : <Radio size="small" checked={checked} />}
                                    <ListItemText primary={name} />
                                </MenuItem>
                            );
                        })}
                        <ListSubheader>Content</ListSubheader>
                        {tagOptions.map(tag => {
                            const checked = selectedTags.includes(tag);
                            return (
                                <MenuItem key={tag} value={tag} dense>
                                    {multiSelect
                                        ? <Checkbox size="small" checked={checked} />
                                        : <Radio size="small" checked={checked} />}
                                    <ListItemText primary={tag} />
                                </MenuItem>
                            );
                        })}
                    </Select>
                </FormControl>
                {!booksLoading && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', ml: 'auto' }}>
                        {sortedFilteredBooks.length} of {visibleBooks.length} titles
                    </Typography>
                )}
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: (window.innerHeight - 430) + 'px', mb: 1, borderTop: '15px solid white' }}>
                {booksLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                        <CircularProgress />
                    </div>
                ) : errorMessage && books.length === 0 ? (
                    <p style={{ color: 'red', padding: '20px' }}>{errorMessage}</p>
                ) : (
                <Table stickyHeader sx={{ minWidth: 800, tableLayout: 'fixed', borderCollapse: 'collapse' }} onClick={handleTableClick}>
                    <colgroup>
                        <col style={{ width: '50px', padding: 0 }} />
                        <col style={{ width: '122px' }} />
                        <col />
                    </colgroup>
                    <TableHead sx={{ position: 'relative', zIndex: 5 }}>
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell>Cover</TableCell>
                            <TableCellSortHead field="title">
                                Title
                            </TableCellSortHead>
                            <TableCellSortHead field="author">
                                Author
                            </TableCellSortHead>
                            <TableCell>
                                Content Tags
                            </TableCell>
                            <TableCellSortHead field="lemmas">
                                Unique Words
                            </TableCellSortHead>
                            <TableCellSortHead field="length">
                                Length
                            </TableCellSortHead>
                            <TableCell>
                                Resources
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ overflowY: 'auto', maxHeight: 200 }}>
                        {sortedFilteredBooks.map((book, index) => {
                            const selected = selectedBookId === book.id;
                            return (
                                <TableRow 
                                    key={index} 
                                    onClick={() => setSelectedBookId(book.id)}
                                    sx={(theme) => ({
                                        '&.MuiTableRow-root': {
                                            border: selected ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
                                            borderBottom: selected ? `1px solid ${theme.palette.primary.main}` : '0px',
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: selected ? theme.palette.primary.light : 'white',
                                            },
                                            '&:nth-of-type(even)': {
                                                backgroundColor: selected ? theme.palette.primary.light : '#EFEFEF',
                                            },
                                            '& .item-cover-image': {
                                                zIndex: showInfo === book ? 6 : 0,
                                                pointerEvents: showInfo === book ? 'none' : 'inherit',
                                            },
                                            /*'& svg': {
                                                zIndex: 4,
                                                opacity: 0,
                                                transition: 'opacity 1000ms'
                                            },
                                            '&:hover svg': {
                                                opacity: 1,
                                            },*/
                                            '&:hover': {
                                                border: `1px solid ${theme.palette.primary.main}`,
                                            },
                                            '&:hover .item-cover-image': {
                                                zIndex: 3,
                                                //filter: 'brightness(0.6)',
                                                transition: "filter 300ms",
                                            },
                                            cursor: 'pointer',
                                            transition: "background-color 300ms",
                                            '& .MuiTableCell-root': {
                                                color: selected ? theme.palette.primary.main : 'inherit',
                                                border: 'none',
                                                position: 'relative',
                                                '&::after': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    bottom: 0,
                                                    backgroundColor: selected || selectedBookId === null ? 'rgba(255, 255, 255, 0.0)' : 'rgba(255, 255, 255, 0.7)',
                                                    zIndex: 2,
                                                    transition: "all 0.3s",
                                                    pointerEvents: 'none',
                                                },
                                            },
                                            '&:hover .MuiTableCell-root': {
                                                '&::after': {
                                                    backgroundColor: 'rgba(255, 255, 255, 0)',
                                                },
                                            },
                                        }
                                    })}
                                >
                                    <TableCell sx={{ p: 1 }}>
                                        <IconButton onClick={event => handleInfoClick(event, book)} style={{padding: 10}}>
                                            <InfoOutlinedIcon sx={{ fontSize: 30 }}/>
                                        </IconButton>
                                    </TableCell>
                                    <TableCell className="cover">
                                        <BookCover url={book.cover}/>
                                    </TableCell>
                                    <TableCell>
                                        {book.title}
                                    </TableCell>
                                    <TableCell>
                                        {book.author}
                                    </TableCell>
                                    <TableCell>
                                        {renderTags(book.tags)}
                                    </TableCell>
                                    <TableCell>
                                        {formatNumberWithSpaces(book.lemmas)}
                                    </TableCell>
                                    <TableCell>
                                        {formatHumanTime(book.duration)}<br/>{formatNumberWithSpaces(book.tokens, 'words')}
                                    </TableCell>
                                    <TableCell>
                                        {renderTags(book.features)}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                )}
                <div
                    style={{
                        position: 'fixed',
                        zIndex: 5,
                        top: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgb(255, 255, 255, 0.8)',
                        pointerEvents: showInfo ? 'inherit' : 'none',
                        opacity: showInfo ? 1 : 0,
                        transition: 'opacity 300ms'
                    }}
                    onClick={onInfoClose}
                >
                    {showInfo && <div style={{
                        marginLeft: '200px', // 'calc(50% - 40px)',
                        border: '1px solid #999',
                        borderRadius: '10px',
                        width: '450px',
                        maxHeight: '520px',
                        backgroundColor: 'white',
                        //transform: 'translateX(-50%)',
                        padding: '30px',
                        overflowY: 'auto',
                        position: 'relative',
                    }}>
                        <IconButton sx={{
                            position: 'absolute',
                            right: '25px',
                            top: '12px',
                        }} edge="end" color="inherit" onClick={null} aria-label="close">
                            <CloseIcon size="28" />
                        </IconButton>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="h5">{showInfo.title}</Typography>
                            <Box>

                            </Box>
                        </Box>
                        <Typography variant="h6" sx={{ mb: 2 }}>{showInfo.author}</Typography>
                        <Typography variant="body1" sx={{ mb: 1 }} dangerouslySetInnerHTML={{ __html: showInfo.description }}/>
                        {/*showInfo.description.split('\n').map((paragraph, index) => (
                            <Typography key={index} variant="body1" sx={{ mb: 1 }} >{paragraph}</Typography>
                        ))*/}
                    </div> }
                </div>
            </TableContainer>

            <Button
                variant="contained"
                color="primary"
                sx={{ mt: 0, mb: 0 }}
                onClick={handleNext}
                disabled={selectedBookId === null}
            >
              Continue
            </Button>
        </Box>
    );
};

export default AssignmentBook;
